import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import NavLink from '../NavLink/navlink-component';
import MediaComponent from '../Media/media-component';
import FavoriteHeart from '../Events/favorite-heart-component';
import { getEventsScreenData } from '../../Public/events-screen-data';
import { parseDate, formatDateRange, isEventLocationApproved } from '../Events/event-date-utils';
import PrivateLocationBadge from '../Events/private-location-badge-component';

/**
 * Event card, DICE-style listing card: square cover with a heart button, then
 * title, a coloured date line, the venue and the price. Sits on the dark
 * events section and inside the preview modal.
 */
const EventCard = ({ event, lang = 'he', className = '' }) => {
    const [fav, setFav] = useState(false);
    const brandInitials = useSelector((s) => (s.systemData?.businessInfo?.businessName || '').trim().charAt(0).toUpperCase());
    if (!event) return null;

    const isRtl = lang !== 'en';
    const { calendar: c, postStrings: t } = getEventsScreenData(lang);
    const href = `/events/${event.slug}`;
    const cover = event.image || event.seo?.image || event.thumbnail;
    const isFree = !event.price || event.price === 0;
    const start = parseDate(event.startDate);

    const dateLabel = (!event.isMultiDay && start)
        ? start.toLocaleDateString(isRtl ? 'he-IL' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        : formatDateRange(event.startDate, event.endDate, lang);

    const toggleFav = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFav((v) => !v);
    };

    return (
        <NavLink
            href={href}
            title={event.title}
            showUnderline={false}
            preserveStyle
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`group flex flex-col cursor-pointer !opacity-100 ${isRtl ? 'text-right' : 'text-left'} ${className}`}
        >
            {/* Cover */}
            <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-primary-900">
                {cover ? (
                    <MediaComponent src={cover} alt={event.title} useSekeleton={false} objectFit="cover" wrapperClassName="w-full h-full" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-800 to-primary-950">
                        <span className="text-3xl font-extralight text-white/25 font-secondary select-none">{brandInitials}</span>
                    </div>
                )}

                {/* Favourite */}
                <FavoriteHeart
                    active={fav}
                    onToggle={toggleFav}
                    size={18}
                    buttonClassName={`absolute bottom-2.5 ${isRtl ? 'left-2.5' : 'right-2.5'} w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-black/75`}
                />
            </div>

            {/* Body */}
            <div className="pt-3">
                <h3 className="text-base md:text-lg font-bold text-white leading-snug line-clamp-2 group-hover:text-white/80 transition-colors font-primary">
                    {event.title}
                </h3>
                <p className="mt-1 text-sm font-bold text-secondary-400 font-primary">{dateLabel}</p>
                {event.location?.name && (
                    isEventLocationApproved(event)
                        ? <p className="mt-0.5 text-sm text-white/70 font-primary line-clamp-1">{event.location.name}</p>
                        : <PrivateLocationBadge lang={lang} variant="pill" className="mt-1.5" />
                )}
                <p className="mt-0.5 text-sm text-white/50 font-primary">
                    {isFree ? c.free : `${t.from} $${event.price}`}
                </p>
            </div>
        </NavLink>
    );
};

export default EventCard;
